import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Instagram } from "lucide-react";
import { COMPANY, FEATURES, waLink } from "@/lib/company";
import { WaIcon } from "@/lib/whatsapp";
import Logo from "@/components/Logo";

const links = [
  { to: "/fahrzeuge", label: "Fahrzeuge" },
  ...(FEATURES.ankauf ? [{ to: "/ankauf", label: "Fahrzeugankauf" }] : []),
  ...(FEATURES.findet ? [{ to: "/fahrzeugsuche", label: "Fahrzeugsuche" }] : []),
  ...(FEATURES.financing ? [{ to: "/finanzierung", label: "Finanzierung" }] : []),
  { to: "/ueber-uns", label: "Über uns" },
  { to: "/kontakt", label: "Kontakt" },
  { to: "/impressum", label: "Impressum" },
  { to: "/datenschutz", label: "Datenschutz" },
];

const linkCls = (transparent) => ({ isActive }) =>
  `relative text-sm font-medium transition-colors duration-300 py-2 after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:rounded-full after:bg-brand after:transition-[width] after:duration-300 ${
    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"} ${
    transparent ? (isActive ? "text-white" : "text-white/75 hover:text-white") : (isActive ? "text-ink" : "text-dim hover:text-ink")}`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const transparent = location.pathname === "/" && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 border-b ${
        transparent ? "bg-transparent border-transparent" : "glass border-line/70"}`}>
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0" aria-label={`${COMPANY.name} Startseite`}>
          <Logo className="h-9 md:h-11" variant={transparent ? "onDark" : "onLight"} />
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.to.replace("/", "")}`} className={linkCls(transparent)}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {COMPANY.instagram && (
            <a href={COMPANY.instagram.url} target="_blank" rel="noopener noreferrer" data-testid="nav-instagram-btn"
              aria-label={`${COMPANY.instagram.handle} auf Instagram`} title={`${COMPANY.instagram.handle} auf Instagram`}
              className={`flex items-center justify-center w-11 h-11 rounded-full border transition-colors duration-300 ${
                transparent ? "border-white/30 text-white hover:border-white" : "border-line text-ink hover:border-ink/40"}`}>
              <Instagram size={18} strokeWidth={1.8} />
            </a>
          )}
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp-btn" aria-label="WhatsApp"
            className={`flex items-center justify-center w-11 h-11 rounded-full border transition-colors duration-300 ${
              transparent ? "border-white/30 text-white hover:border-white" : "border-line text-ink hover:border-ink/40"}`}>
            <WaIcon size={18} />
          </a>
          <a href={COMPANY.phoneLink} data-testid="nav-phone-btn"
            className={`btn-pill inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold ${
              transparent ? "bg-white text-black hover:bg-white/90" : "bg-action text-action-ink hover:bg-action-hover"}`}>
            <Phone size={15} strokeWidth={2} /> <span className="hidden xl:inline">{COMPANY.phone}</span><span className="xl:hidden">Anrufen</span>
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          {COMPANY.instagram && (
            <a href={COMPANY.instagram.url} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-instagram-icon"
              aria-label={`${COMPANY.instagram.handle} auf Instagram`} className={`p-2 ${transparent ? "text-white" : "text-ink"}`}>
              <Instagram size={22} strokeWidth={1.8} />
            </a>
          )}
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Menü"
            className={`p-2 ${transparent ? "text-white" : "text-ink"}`}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden glass border-t border-line/70 px-6 py-7 flex flex-col gap-5 fade-up max-h-[calc(100vh-4rem)] overflow-y-auto">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-mobile-link-${l.to.replace("/", "")}`}
              className={({ isActive }) => `font-display text-2xl font-medium ${isActive ? "text-brand" : "text-ink"}`}>
              {l.label}
            </NavLink>
          ))}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <a href={COMPANY.phoneLink} data-testid="nav-mobile-phone-btn"
              className="btn-pill inline-flex items-center justify-center gap-2 py-3.5 bg-action text-action-ink font-semibold text-sm">
              <Phone size={16} /> Anrufen
            </a>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-whatsapp-btn"
              className="btn-pill inline-flex items-center justify-center gap-2 py-3.5 border border-line bg-surface text-ink font-semibold text-sm">
              <WaIcon size={16} className="text-[#25D366]" /> WhatsApp
            </a>
            {COMPANY.instagram && (
              <a href={COMPANY.instagram.url} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-instagram-btn"
                className="col-span-2 btn-pill inline-flex items-center justify-center gap-2 py-3.5 border border-line bg-surface text-ink font-semibold text-sm">
                <Instagram size={16} strokeWidth={1.8} className="text-[#E1306C]" /> {COMPANY.instagram.handle} auf Instagram
              </a>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
